import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus, AlertTriangle } from "lucide-react";
import { CustomerForm, blankCustomer as blank } from "@/components/customers/CustomerForm";
import { walletName } from "@/lib/wallet";
import { effectiveCreditMode, CREDIT_MODE_LABELS, defaultStoredModeForForm } from "@/lib/creditMode";
import ManagerPinModal from "@/components/ManagerPinModal";


export default function Customers() {
  const { user, business } = useAuth();
  const modules = useModules();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [pinOpen, setPinOpen] = useState(false); const [pinMsg, setPinMsg] = useState(""); const [pinTitle, setPinTitle] = useState("Credit Setup Approval");
  const [saving, setSaving] = useState(false);
  const openNew = () => { setForm({ ...blank, credit_mode: defaultStoredModeForForm(business) }); setOpen(true); };

  const load = () => api.get("/customers", { params: { search, status } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [search, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (pin = null) => {
    if (saving) return;
    try {
      if (!form.name.trim()) { toast.error("Customer name is required"); return; }
      if (!form.phone?.trim()) { toast.error("A phone number is required for new customers"); return; }
      const v = await api.get("/customers/phone/validate", { params: { phone: form.phone, region: form.phone_region || "JM" } });
      if (v.data.status !== "valid") {
        toast.error(v.data.status === "ambiguous_short"
          ? "Add the area code — a 7-digit number alone isn't accepted for new customers"
          : "Please enter a valid phone number");
        return;
      }
      setSaving(true);
      await api.post("/customers", { ...form, source: "customers_page", manager_pin: pin });
      toast.success("Customer created"); setOpen(false); setPinOpen(false); setForm(blank); load();
    } catch (e) { const d = e.response?.data?.detail; if (d?.needs_approval) { setPinMsg(d.message); if (d.reason !== "approval_failed" && d.reason !== "approval_cooldown") setPinTitle(d.title || (d.owner_pin_required ? "Owner Approval Required" : "Credit Setup Approval")); setPinOpen(true); } else toast.error(apiError(d)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold" data-testid="customers-heading">Customers</h1>
          <p className="text-muted-foreground">Customer details, credit / A/R, {modules.isEnabled("prepaid_accounts") ? "Wallet, " : ""}payments, statements and purchase history.</p>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Customer
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customers…" className="pl-9" data-testid="customer-search" />
        </div>
        {[["active", "Active"], ["hold", "On Hold"], ["closed", "Closed"], ["all", "All"]].map(([v, l]) => (
          <Button key={v} size="sm" variant={status === v ? "default" : "outline"} className={status === v ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""} onClick={() => setStatus(v)} data-testid={`customer-filter-${v}`}>{l}</Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((c) => {
          const eff = effectiveCreditMode(c);
          const usage = eff === "credit_limit" && c.credit_limit > 0 ? Math.min(100, (c.outstanding_balance / c.credit_limit) * 100) : 0;
          const overdue = c.max_days_overdue > 0;
          return (
            <Card key={c.id} onClick={() => nav(`/customers/${c.id}`)} data-testid={`customer-card-${c.id}`}
              className="p-5 cursor-pointer hover:border-emerald-500/40 hover:shadow-lg transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center font-head font-bold text-lg">{c.name[0]}</div>
                  <div>
                    <p className="font-head font-bold leading-tight">{c.name}</p>
                    <p className="text-xs text-muted-foreground">{c.company || c.phone || "-"}</p>
                  </div>
                </div>
                {overdue ? <Badge className="bg-rose-500/15 text-rose-500 border-rose-500/30"><AlertTriangle className="w-3 h-3 mr-1" />Overdue</Badge>
                  : c.account_status !== "active" ? <Badge className="bg-slate-500/15 text-slate-500 border-slate-500/30 capitalize">{c.account_status}</Badge>
                  : <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">Good</Badge>}
              </div>
              <div className="mt-4 space-y-1.5 text-sm" data-testid={`customer-card-features-${c.id}`}>
                {c.phone && <p className="text-xs text-muted-foreground">{c.phone}{c.email ? ` · ${c.email}` : ""}</p>}
                {eff === "open_account" && <div className="flex justify-between" data-testid={`customer-card-mode-${c.id}`}><span className="text-violet-500">Credit · {CREDIT_MODE_LABELS.open_account}</span><span className="text-xs text-muted-foreground">{c.credit_terms_days}-day terms</span></div>}
                {eff === "credit_limit" && <><div className="flex justify-between" data-testid={`customer-card-mode-${c.id}`}><span className="text-violet-500">Credit available</span><span className={`mono font-bold ${c.available_credit != null && c.available_credit < 0 ? "text-rose-500" : ""}`}>{money(c.available_credit ?? 0)}</span></div><Progress value={usage} className="h-1.5" /></>}
                {c.outstanding_balance > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Amount owed</span><span className="mono font-bold">{money(c.outstanding_balance)}</span></div>}
                {c.wallet_enabled && modules.isEnabled("prepaid_accounts") && <p className="text-xs text-sky-600">{walletName(business)} enabled</p>}
                {!c.credit_enabled && !(c.outstanding_balance > 0) && !c.wallet_enabled && <p className="text-xs text-muted-foreground" data-testid={`customer-card-regular-${c.id}`}>Regular customer</p>}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-form">
          <DialogHeader><DialogTitle className="font-head">New Customer</DialogTitle></DialogHeader>
          <CustomerForm form={form} setForm={setForm} business={business} mode="create" />
          <Button onClick={() => save()} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-customer">{saving ? "Creating…" : "Create Customer"}</Button>
        </DialogContent>
      </Dialog>
      <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)} title={pinTitle} reason={pinMsg} onSubmit={(pin) => save(pin)} />
    </div>
  );
}

